import { prductData } from "../Data";
import { homeOnPageContent } from "../data/onPageContent";
import { getServiceBySlug, serviceCategories } from "../data/serviceData";
import { absoluteUrl, assetUrl, siteConfig } from "../siteConfig";

export const staticPageSeo = {
  "/": {
    title: siteConfig.defaultTitle,
    description: siteConfig.defaultDescription,
    image: siteConfig.defaultImage,
    imageAlt: "Recon Calibration and Test Labs accredited calibration laboratory",
    pageType: "WebPage",
    pageName: "Calibration Services in Pune and India",
    keywords:
      "calibration services in Pune, calibration services in India, NABL accredited calibration laboratory, ISO IEC 17025 calibration",
    faqs: homeOnPageContent.faqs,
  },
  "/about": {
    title: `About Us | ${siteConfig.siteName}`,
    description:
      "Learn about Recon Calibration & Test Labs LLP, an ISO/IEC 17025:2017 NABL accredited calibration laboratory in Pune delivering reliable calibration solutions across India.",
    image: assetUrl("/img/about.jpg"),
    imageAlt: "About Recon Calibration and Test Labs LLP",
    pageType: "AboutPage",
    pageName: "About Us",
    keywords:
      "about Recon Calibration, NABL accredited laboratory Pune, ISO IEC 17025 calibration laboratory",
  },
  "/services": {
    title: `Calibration Services in Pune and India | ${siteConfig.siteName}`,
    description:
      "Explore electrical, dimensional, pressure, mass, volume, thermal, force and hardness calibration services from Recon Calibration & Test Labs LLP in Pune and across India.",
    image: assetUrl("/img/section-capabilities-premium.jpg"),
    imageAlt: "Recon accredited calibration service capabilities",
    pageType: "CollectionPage",
    pageName: "Calibration Services",
    keywords:
      "electrical calibration, dimensional calibration, pressure calibration, mass calibration, volume calibration, thermal calibration, force calibration, hardness calibration",
  },
  "/certificates": {
    title: `NABL Certificate NABLC0626MH04748 | ${siteConfig.siteName}`,
    description:
      "View NABL accreditation certificate NABLC0626MH04748 and its scope for Recon Calibration & Test Labs LLP, valid from 06/06/2026 to 05/06/2030.",
    image: assetUrl("/img/NABL-Certificate-NABLC0626MH04748.png"),
    imageAlt: "NABL Certificate NABLC0626MH04748",
    pageType: "WebPage",
    pageName: "NABL Accreditation Certificate",
    keywords:
      "NABL certificate NABLC0626MH04748, Recon Calibration accreditation, ISO IEC 17025 certificate",
  },
  "/gallery": {
    title: `Calibration Laboratory Gallery | ${siteConfig.siteName}`,
    description:
      "Browse the Recon Calibration & Test Labs LLP gallery featuring our laboratory, calibration equipment, facilities and company work highlights.",
    image: assetUrl("/img/gallery.png"),
    imageAlt: "Recon Calibration laboratory gallery",
    pageType: "CollectionPage",
    pageName: "Calibration Laboratory Gallery",
    keywords:
      "calibration laboratory gallery, calibration equipment Pune, Recon Calibration laboratory",
  },
  "/contact": {
    title: `Contact Calibration Laboratory in Pune | ${siteConfig.siteName}`,
    description:
      "Contact Recon Calibration & Test Labs LLP in Pune for NABL accredited calibration services, onsite support, quotations and technical assistance across India.",
    image: assetUrl("/img/contactbanner.jpeg"),
    imageAlt: "Contact Recon Calibration and Test Labs LLP",
    pageType: "ContactPage",
    pageName: "Contact Us",
    keywords:
      "contact calibration laboratory Pune, calibration quotation, onsite calibration support India",
  },
  "/privacy": {
    title: `Privacy Policy | ${siteConfig.siteName}`,
    description:
      "Read the privacy policy and website terms for Recon Calibration & Test Labs LLP.",
    image: assetUrl("/img/privacy.jpg"),
    imageAlt: "Recon Calibration privacy policy",
    pageType: "WebPage",
    pageName: "Privacy Policy",
    keywords: "Recon Calibration privacy policy, website privacy terms",
  },
};

const normalizePathname = (pathname = "/") => {
  const cleanPath = pathname.split("?")[0].split("#")[0] || "/";
  return cleanPath !== "/" ? cleanPath.replace(/\/+$/, "") : "/";
};

export const resolveSeo = (requestedPathname) => {
  const pathname = normalizePathname(requestedPathname);

  if (pathname.startsWith("/services/")) {
    const serviceSlug = pathname.split("/")[2];
    const service = getServiceBySlug(serviceSlug);

    if (service) {
      const serviceTitle = `${service.name} Services in Pune | ${service.name} Services in India | Recon Calibration`;
      const description =
        service.metaDescription ||
        `Get ISO/IEC 17025:2017 NABL accredited ${service.name} services in Pune and across India from Recon Calibration & Test Labs LLP.`;

      return {
        title: service.metaTitle || serviceTitle,
        description,
        image: assetUrl(service.heroImage),
        imageAlt: `${service.name} services from Recon Calibration and Test Labs LLP`,
        canonical: absoluteUrl(pathname),
        pageType: "WebPage",
        pageName: service.pageTitle || `${service.name} Services`,
        keywords: `${service.name} services in Pune, ${service.name} services in India, NABL accredited ${service.name}, ISO IEC 17025 calibration`,
        ogTitle:
          service.ogTitle || `${service.name} Services in Pune | ${siteConfig.siteName}`,
        ogDescription: service.ogDescription || description,
        twitterTitle:
          service.twitterTitle || `${service.name} Services in Pune`,
        twitterDescription: service.twitterDescription || description,
        service,
        faqs: service.faqs,
      };
    }
  }

  if (pathname.startsWith("/product/")) {
    const productId = pathname.split("/")[2];
    const product = prductData.find((item) => String(item.id) === productId);

    if (product) {
      return {
        title: `${product.title} | ${siteConfig.siteName}`,
        description: product.description,
        image: assetUrl(product.image),
        imageAlt: product.title,
        canonical: absoluteUrl(pathname),
        pageType: "WebPage",
        pageName: product.title,
        keywords: `${product.title}, calibration instruments, measurement equipment Pune`,
        ogType: "product",
        product,
      };
    }
  }

  const seo = staticPageSeo[pathname] || staticPageSeo["/"];

  return {
    ...seo,
    canonical: absoluteUrl(pathname),
  };
};

export const buildOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${siteConfig.baseUrl}/#organization`,
  name: siteConfig.siteName,
  legalName: siteConfig.legalName,
  url: `${siteConfig.baseUrl}/`,
  logo: {
    "@type": "ImageObject",
    url: siteConfig.logo,
  },
  image: siteConfig.defaultImage,
  description: siteConfig.defaultDescription,
  email: siteConfig.email,
  telephone: siteConfig.schemaPhone,
  foundingDate: siteConfig.foundingDate,
  taxID: siteConfig.taxId,
  address: {
    "@type": "PostalAddress",
    ...siteConfig.address,
  },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: siteConfig.schemaPhone,
    contactType: "customer service",
    areaServed: "IN",
    availableLanguage: ["English", "Hindi", "Marathi"],
  },
});

export const buildLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${siteConfig.baseUrl}/#localbusiness`,
  name: siteConfig.siteName,
  url: `${siteConfig.baseUrl}/`,
  image: siteConfig.defaultImage,
  logo: siteConfig.logo,
  description: siteConfig.defaultDescription,
  email: siteConfig.email,
  telephone: siteConfig.schemaPhone,
  priceRange: siteConfig.priceRange,
  address: {
    "@type": "PostalAddress",
    ...siteConfig.address,
  },
  geo: {
    "@type": "GeoCoordinates",
    ...siteConfig.geo,
  },
  areaServed: {
    "@type": "Country",
    name: siteConfig.areaServed,
  },
  openingHours: "Mo-Sa 09:00-18:00",
  parentOrganization: {
    "@id": `${siteConfig.baseUrl}/#organization`,
  },
});

export const buildWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${siteConfig.baseUrl}/#website`,
  name: siteConfig.siteName,
  url: `${siteConfig.baseUrl}/`,
  description: siteConfig.defaultDescription,
  publisher: {
    "@id": `${siteConfig.baseUrl}/#organization`,
  },
  inLanguage: "en-IN",
});

export const buildBreadcrumbSchema = (requestedPathname, pageName) => {
  const pathname = normalizePathname(requestedPathname);
  const items = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${siteConfig.baseUrl}/`,
    },
  ];

  if (pathname.startsWith("/services/")) {
    items.push({
      "@type": "ListItem",
      position: 2,
      name: "Calibration Services",
      item: absoluteUrl("/services"),
    });
    items.push({
      "@type": "ListItem",
      position: 3,
      name: pageName,
      item: absoluteUrl(pathname),
    });
  } else if (pathname.startsWith("/product/")) {
    items.push({
      "@type": "ListItem",
      position: 2,
      name: "Products",
      item: absoluteUrl("/"),
    });
    items.push({
      "@type": "ListItem",
      position: 3,
      name: pageName,
      item: absoluteUrl(pathname),
    });
  } else if (pathname !== "/") {
    items.push({
      "@type": "ListItem",
      position: 2,
      name: pageName,
      item: absoluteUrl(pathname),
    });
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${absoluteUrl(pathname)}#breadcrumb`,
    itemListElement: items,
  };
};

export const buildPageSchema = (requestedPathname, seo) => {
  const pathname = normalizePathname(requestedPathname);

  return {
    "@context": "https://schema.org",
    "@type": seo.pageType,
    "@id": `${seo.canonical}#webpage`,
    name: seo.pageName,
    headline: seo.title,
    url: seo.canonical,
    description: seo.description,
    isPartOf: {
      "@id": `${siteConfig.baseUrl}/#website`,
    },
    about: {
      "@id": `${siteConfig.baseUrl}/#organization`,
    },
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: seo.image,
      caption: seo.imageAlt,
    },
    breadcrumb: {
      "@id": `${absoluteUrl(pathname)}#breadcrumb`,
    },
    inLanguage: "en-IN",
  };
};

export const buildServiceSchema = (seo) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${seo.canonical}#service`,
  name: `${seo.service.name} Services`,
  serviceType: seo.service.name,
  description: seo.description,
  url: seo.canonical,
  areaServed: {
    "@type": "Country",
    name: "India",
  },
  provider: {
    "@id": `${siteConfig.baseUrl}/#organization`,
  },
  offers: {
    "@type": "Offer",
    availability: "https://schema.org/InStock",
    url: seo.canonical,
  },
});

export const buildFaqSchema = (canonical, faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${canonical}#faq`,
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

export const buildProductSchema = (seo, product) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  "@id": `${seo.canonical}#product`,
  name: product.title,
  description: product.description,
  image: seo.image,
  brand: {
    "@type": "Brand",
    name: siteConfig.siteName,
  },
  manufacturer: {
    "@id": `${siteConfig.baseUrl}/#organization`,
  },
  url: seo.canonical,
});

const buildServiceCatalogSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "@id": `${absoluteUrl("/services")}#service-list`,
  name: "Calibration Services",
  itemListElement: serviceCategories.map((service, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: service.name,
    url: absoluteUrl(`/services/${service.slug}`),
  })),
});

const buildCertificateSchema = (seo) => ({
  "@context": "https://schema.org",
  "@type": "CreativeWork",
  "@id": `${seo.canonical}#certificate`,
  name: "NABL Certificate NABLC0626MH04748",
  identifier: "NABLC0626MH04748",
  description: seo.description,
  url: seo.canonical,
  image: seo.image,
  about: {
    "@id": `${siteConfig.baseUrl}/#organization`,
  },
});

export const buildSchemas = (requestedPathname, seo = resolveSeo(requestedPathname)) => {
  const pathname = normalizePathname(requestedPathname);
  const schemas = [
    buildOrganizationSchema(),
    buildLocalBusinessSchema(),
    buildWebsiteSchema(),
    buildPageSchema(pathname, seo),
    buildBreadcrumbSchema(pathname, seo.pageName),
  ];

  if (seo.service) {
    schemas.push(buildServiceSchema(seo));
  }

  if (seo.faqs?.length) {
    schemas.push(buildFaqSchema(seo.canonical, seo.faqs));
  }

  if (seo.product) {
    schemas.push(buildProductSchema(seo, seo.product));
  }

  if (pathname === "/services") {
    schemas.push(buildServiceCatalogSchema());
  }

  if (pathname === "/certificates") {
    schemas.push(buildCertificateSchema(seo));
  }

  return schemas;
};

export const allKnownRoutes = [
  ...Object.keys(staticPageSeo),
  ...serviceCategories.map((service) => `/services/${service.slug}`),
  ...prductData.map((product) => `/product/${product.id}`),
];

